import { useEffect, useState } from "react";
import { api, ApiError, type Billing } from "../api";
import { useAsync } from "../useAsync";
import { getSession } from "../session";
import { go } from "../router";
import { track } from "../lib/analytics";

function money(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}

function meter(used: number, cap: number | null): string {
  if (cap === null) return `${used.toLocaleString()} / unlimited`;
  return `${used.toLocaleString()} / ${cap.toLocaleString()}`;
}

function planName(id: Billing["plan"]): string {
  return id === "free" ? "Free" : id === "indie" ? "Indie" : "Growth";
}

/**
 * What the workspace actually pays for: one plan, and one line per product
 * past its own trial. A product still in trial is listed with its countdown so
 * the next invoice is never a surprise.
 */
function ProductLines({ b }: { b: Billing }) {
  if (b.product_lines.length === 0) return null;
  const price = b.plans.find((p) => p.id === b.plan)?.price_cents ?? 0;
  return (
    <div className="panel" style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
      <div className="eyebrow">Projects on this plan</div>
      {b.product_lines.map((line) => (
        <div key={line.slug} style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <span style={{ flex: 1 }}>{line.name}</span>
          <span className="sub num">{line.events.toLocaleString()} events</span>
          <span className="num">
            {line.billable
              ? price === 0 ? "included" : `${money(price)}/mo`
              : `free · ${line.trial_days_left ?? 0}d left`}
          </span>
        </div>
      ))}
      {b.plan !== "free" && (
        <span className="sub">
          Billing {b.quantity} × {money(price)}/mo
          {b.billable_products < b.quantity && " — the minimum while every project is still in trial"}.
        </span>
      )}
    </div>
  );
}

export function Billing() {
  const data = useAsync(() => api.billing.get(), []);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const owner = getSession()?.account.member_role !== "member";

  useEffect(() => {
    const q = location.hash.split("?")[1] ?? "";
    if (!q.includes("upgraded=1")) return;
    track("paid_subscription");
    setNote("Checkout complete — refreshing your plan…");
    let n = 0;
    const t = setInterval(() => {
      data.reload();
      n += 1;
      if (n >= 8) clearInterval(t);
    }, 1500);
    return () => clearInterval(t);
  }, [data]);

  const checkout = async (plan: "indie" | "growth") => {
    setBusy(plan);
    setNote(null);
    try {
      const { url } = await api.billing.checkout(plan);
      location.href = url;
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : String(e));
      setBusy(null);
    }
  };

  const portal = async () => {
    setBusy("portal");
    setNote(null);
    try {
      const { url } = await api.billing.portal();
      location.href = url;
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : String(e));
      setBusy(null);
    }
  };

  if (data.loading && !data.data) return <div style={{ color: "var(--text-faint)", fontSize: 13, marginTop: 20 }}>Loading billing…</div>;
  if (data.error) return <div className="form-error" style={{ marginTop: 20 }}>{data.error}</div>;
  const b = data.data!;

  return (
    <div style={{ maxWidth: 820 }}>
      <div style={{ fontSize: 13.5, color: "var(--text-2)", marginTop: 14 }}>
        {b.plan_status === "trialing" && b.trial_days_left !== null
          ? `Growth trial — ${b.trial_days_left} day${b.trial_days_left === 1 ? "" : "s"} left. No card required.`
          : `You're on ${b.plan === "free" ? "Free" : b.plan === "indie" ? "Indie" : "Growth"}.`}
      </div>

      <div className="panel" style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="eyebrow">This period ({b.period})</div>
        <div>Projects · {meter(b.usage.products, b.limits.products)}</div>
        <div>Seats · {meter(b.usage.seats, b.limits.seats)}</div>
        <div>Tracked events · {meter(b.usage.events, b.limits.events)} per project</div>
        {b.features.ai_advanced && (
          <div>Landing-page analyses · {meter(b.usage.analyses, b.limits.analyses)} per project</div>
        )}
        {b.limits.history_months !== null && (
          <div className="sub">History window: {b.limits.history_months} months. Older facts stay stored — upgrade to see them.</div>
        )}
      </div>

      <ProductLines b={b} />

      <div className="panel" style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="eyebrow">Add a project</div>
        <span className="sub">
          {b.limits.products !== null
            ? `${planName(b.plan)} includes ${b.limits.products} project. Upgrade to add more.`
            : "Every project is free for its first 30 days, then it's one more project at the plan price on the same subscription."}
        </span>
        <div>
          <button type="button" className="btn-ghost" onClick={() => go({ name: "newProduct" })}>
            New project
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 22 }}>
        {b.plans.map((p) => {
          const current = p.id === b.plan;
          return (
            <div key={p.id} className="panel" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ fontWeight: 600 }}>{p.name}</div>
              <div className="num">{p.price_cents === 0 ? "$0" : `${money(p.price_cents)}/mo`}</div>
              {current && <span className="eyebrow">current</span>}
              {owner && !current && p.id !== "free" && b.billing_available && (
                <button type="button" className="btn-solid" disabled={busy !== null}
                  onClick={() => void checkout(p.id as "indie" | "growth")}>
                  {busy === p.id ? "Redirecting…" : current ? "Current" : `Choose ${p.name}`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {owner && b.has_customer && b.billing_available && (
        <div style={{ marginTop: 18 }}>
          <button type="button" className="btn-ghost" disabled={busy !== null} onClick={() => void portal()}>
            {busy === "portal" ? "Opening…" : "Manage subscription"}
          </button>
        </div>
      )}
      {!b.billing_available && (
        <div className="sub" style={{ marginTop: 16 }}>Billing checkout is not configured on this server yet.</div>
      )}
      {!owner && <div className="sub" style={{ marginTop: 16 }}>Only owners can change the plan.</div>}
      {note && <div className="form-note" style={{ marginTop: 12 }}>{note}</div>}
    </div>
  );
}
